<script lang="ts">
export interface SearchResult {
  id: number
  name: string
  url: string
}

export interface SearchState {
  search: string
}
</script>

<script setup lang="ts">
const props = defineProps(['model'])
const state = reactive<SearchState>({
  search: '',
})

const route = useRoute()
const router = useRouter()
const query = ref<string>('')
const fetchResults = ref<any>(null)
const results = ref<SearchResult[]>([])
const perPage = 10
const loading = ref<boolean>(false)
const currentPage = ref<number>(Number(route.query.page) || 1)
const hasSearchBeenPerformed = ref(false)

watch(() => route.query.page, (page) => {
  currentPage.value = Number(page) || 1

  nextTick(() => {
    document.getElementById('results')?.scrollIntoView({ behavior: 'smooth' })
  })
})

function paginationLink(page: number) {
  router.push({
    path: route.path,
    query: {
      ...route.query,
      page,
    },
    hash: '#results',
  })
}

const paginatedResults = computed(() => {
  const start = (currentPage.value - 1) * perPage
  return results.value.slice(start, start + perPage)
})

const topQuestions = computed(() => {
  return props?.model?.topQuestions?.expandedValue?.map((tq: any): any => ({
    text: tq.pageTitle?.value,
    url: tq.contentLink?.url,
  }))
})

async function handleSearch() {
  loading.value = true
  currentPage.value = 1
  hasSearchBeenPerformed.value = true

  results.value = await search(query.value)
  loading.value = false
}

// Search function
async function search(searchQuery: string): Promise<SearchResult[]> {
  if (searchQuery === '') {
    return []
  }

  // Fetch questions from API
  const { data } = await useFetch<any>('/api/questions/search', {
    query: {
      searchTerm: query.value,
    },
    method: 'GET',
  })

  fetchResults.value = data.value

  // Process the search results
  if (fetchResults.value && fetchResults.value.questions && Array.isArray(fetchResults.value.questions)) {
    // Map API response to SearchResult interface
    return fetchResults.value.questions.map((question: any): SearchResult => ({
      id: question.url,
      name: question.questionTitle,
      url: question.url,
    }))
  }
  else {
    console.error('Error loading search results')
    return []
  }
}

function clearSearchInputText() {
  query.value = ''
  const searchInput = document.getElementById('searchInput') as HTMLInputElement
  searchInput?.focus()
}

function resetSearch() {
  query.value = ''
  results.value = []
  currentPage.value = 1
  hasSearchBeenPerformed.value = false

  const newQuery = { ...route.query }
  delete newQuery.page
  delete newQuery.query

  router.replace({
    path: route.path,
    query: newQuery,
  })
}
</script>

<template>
  <div class="question-search-page">
    <HeroBlock
      :variation="model.pageHeroContent?.heroStyle?.value"
      :color="model.pageHeroContent?.heroColor?.value"
      :title="model.pageHeroContent?.heroTitle?.value"
      :image="model.pageHeroContent?.aprimoHeroImage?.value?.url"
      :image-alt-text="model.pageHeroContent?.heroImageAltText?.value"
      :image-width="model.pageHeroContent?.aprimoHeroImage?.value?.width"
      :image-height="model.pageHeroContent?.aprimoHeroImage?.value?.height"
    >
      <UForm
        :state="state"
        class="mt-10 max-w-177.5 ml-auto mr-auto"
        @submit="handleSearch"
      >
        <UInput
          id="searchInput"
          v-model="query"
          placeholder="Search for answers..."
          size="lg"
          :ui="{
            base: 'hero__search text-md pt-3 pb-3 pl-6 pr-10 rounded-2xl bg-white',
          }"
        >
          <template #trailing>
            <button
              v-show="query"
              type="button"
              class="text-med-deep-navy pr-2 hover:opacity-80 cursor-pointer"
              aria-label="Clear"
              @click="clearSearchInputText"
            >
              <UIcon
                name="i-heroicons-x-mark"
                class="w-5 h-5 [&>path]:stroke-2"
              />
            </button>

            <button
              type="submit"
              class="text-med-deep-navy pr-2 hover:opacity-80 cursor-pointer"
              aria-label="Search"
            >
              <UIcon
                name="i-heroicons-magnifying-glass"
                class="w-5 h-5 [&>path]:stroke-2"
              />
            </button>
          </template>
        </UInput>
      </UForm>
    </HeroBlock>
    <div class="container-fluid">
      <div class="main-body">
        <div
          id="results"
          class="block-spacing mx-auto w-full max-w-238.5"
          aria-live="polite"
        >
          <div
            v-if="results.length"
            class="flex flex-wrap mb-7 md:mb-12"
          >
            <div class="text-med-deep-navy italic mr-4">
              Showing {{ paginatedResults.length }} of {{ results.length }} results
            </div>

            <button
              type="button"
              class="block ml-auto text-med-deep-navy hover:opacity-80 underline italic cursor-pointer"
              @click="resetSearch"
            >
              Clear and Reset Search
            </button>
          </div>

          <!-- If no results found after a search show below -->
          <div
            v-if="results.length === 0 && !loading && query && hasSearchBeenPerformed"
            class="prose"
          >
            <h2
              class="text-2xl font-bold text-pretty"
            >
              We're sorry, there are no results!
            </h2>
            <p
              class="mb-0!"
            >
              Your current search has returned with no results.
            </p>
          </div>

          <div
            v-if="loading"
            class="mt-6"
          >
            Loading<span aria-hidden="true">...</span>
          </div>

          <div
            v-else
            class="mt-6"
          >
            <ul>
              <li
                v-for="item in paginatedResults"
                :key="item.id"
                class="border-b-2 border-blue-100 pb-8 mb-8"
              >
                <ULink
                  :to="useCmsUrl(item.url)"
                  :locale="false"
                  class="text-med-deep-navy underline font-bold"
                >
                  {{ item.name }}
                </ULink>
              </li>
            </ul>
            <PaginationBlock
              v-if="results.length"
              :total="results.length"
              :items-per-page="perPage"
              :current-page="currentPage"
              :on-page-change="paginationLink"
            />
          </div>
        </div>
      </div>

      <div
        v-show="!results.length"
        class="page-block-area flex flex-col items-center justify-center eight-col-max mx-auto"
      >
        <GenericsContentArea :model="model.pageBlockArea.expandedValue" />
      </div>
      <ThreeColumnLinkList
        title="Top Questions:"
        width="wide"
        :items="topQuestions"
      />
    </div>
  </div>
</template>

<style lang="scss">
@use '@/assets/css/pages/general-page.scss';
</style>
